import { useEffect, useState } from 'react';
import { Alert, Button, Card, Group, NumberInput, SimpleGrid, Stack, Tabs, Text } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { api } from '@/api/client';
import { lockerState, useLockersHealth } from '@/api/lockers';
import { LockerCard } from '@/components/LockerCard';
import { Page, QueryState } from '@/components/Page';
import { LEGACY_ADMIN_URL } from '@/navigation';
import { notifyError, notifySuccess } from '@/lib/notify';

const RANK = { offline: 0, hw: 1, online: 2 } as const;

function LockerList() {
    const navigate = useNavigate();
    const { data, isLoading, error, refetch } = useLockersHealth();
    const sorted = [...(data ?? [])].sort(
        (a, b) =>
            RANK[lockerState(a)] - RANK[lockerState(b)] ||
            (a.display_name || a.id).localeCompare(b.display_name || b.id, 'ru')
    );
    return (
        <QueryState isLoading={isLoading} error={error} onRetry={refetch}>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
                {sorted.map((l) => (
                    <LockerCard key={l.id} locker={l} onClick={() => navigate(`/lockers/${l.id}`)} />
                ))}
            </SimpleGrid>
        </QueryState>
    );
}

interface QrConfig {
    qr_rotation_interval_seconds: number;
    qr_token_ttl_seconds: number;
}

function QrRules() {
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['qr-config'],
        queryFn: () => api.get<{ config: QrConfig }>('/api/admin/qr-config').then((r) => r.config),
    });
    const [rotation, setRotation] = useState<number | string>('');
    const [ttl, setTtl] = useState<number | string>('');

    useEffect(() => {
        if (!data) return;
        setRotation(Math.round(data.qr_rotation_interval_seconds / 60));
        setTtl(Math.round(data.qr_token_ttl_seconds / 60));
    }, [data]);

    const save = useMutation({
        mutationFn: () =>
            api.post('/api/admin/qr-config', {
                qr_rotation_interval_seconds: Number(rotation) * 60,
                qr_token_ttl_seconds: Number(ttl) * 60,
            }),
        onSuccess: () => notifySuccess('Правила QR сохранены'),
        onError: notifyError,
    });

    const valid = Number(rotation) > 0 && Number(ttl) > 0;

    return (
        <QueryState isLoading={isLoading} error={error} onRetry={refetch}>
            <Card maw={520}>
                <Stack>
                    <Text size="sm" c="dimmed">
                        Как часто постаматы получают новый QR и сколько живёт выданный токен. Действует на все постаматы.
                    </Text>
                    <NumberInput label="Смена QR, мин" min={1} value={rotation} onChange={setRotation} />
                    <NumberInput
                        label="Срок жизни токена, мин"
                        description="Должен быть не меньше интервала смены, иначе клиент не успеет отсканировать"
                        min={1}
                        value={ttl}
                        onChange={setTtl}
                    />
                    <Group justify="flex-end">
                        <Button disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
                            Сохранить
                        </Button>
                    </Group>
                </Stack>
            </Card>
        </QueryState>
    );
}

export function LockersScreen() {
    return (
        <Page title="Постаматы">
            <Tabs defaultValue="list" keepMounted={false}>
                <Tabs.List mb="md">
                    <Tabs.Tab value="list">Список</Tabs.Tab>
                    <Tabs.Tab value="qr">Правила QR</Tabs.Tab>
                    <Tabs.Tab value="apk">Обновления APK</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="list">
                    <LockerList />
                </Tabs.Panel>
                <Tabs.Panel value="qr">
                    <QrRules />
                </Tabs.Panel>
                <Tabs.Panel value="apk">
                    <Alert title="Пока в старой админке" maw={520}>
                        <Stack gap="sm" align="flex-start">
                            Загрузка APK и обновление планшетов ещё не перенесены.
                            <Button
                                component="a"
                                href={`${LEGACY_ADMIN_URL}/lockers#apk`}
                                target="_blank"
                                rel="noopener"
                                variant="light"
                                rightSection={<ExternalLink size={14} />}
                            >
                                Открыть
                            </Button>
                        </Stack>
                    </Alert>
                </Tabs.Panel>
            </Tabs>
        </Page>
    );
}
